import apiClient from './client';
import { EventDto } from '../types/shared';

export interface CreateEventPayload {
  title: string;
  startTime: string;
  endTime: string;
  recurrence?: EventDto['recurrence'];
  force?: boolean;
}

export interface UpdateEventPayload {
  title?: string;
  startTime?: string;
  endTime?: string;
  recurrence?: EventDto['recurrence'];
}

export interface GoogleCalendarEventSummary {
  googleEventId: string;
  title: string;
  startTime: string;
  endTime: string;
  isAllDay: boolean;
}

export const eventsApi = {
  async list(from: string, to: string, source?: EventDto['source']): Promise<EventDto[]> {
    const { data } = await apiClient.get<{ events: EventDto[] }>('/events', {
      params: { from, to, source },
    });
    return data.events;
  },

  async listGoogleUpcoming(): Promise<{ events: GoogleCalendarEventSummary[]; linked: boolean }> {
    const { data } = await apiClient.get<{ events: GoogleCalendarEventSummary[]; linked: boolean }>(
      '/events/google/upcoming',
    );
    return data;
  },

  async listGoogleInRange(from: string, to: string): Promise<{ events: GoogleCalendarEventSummary[]; linked: boolean }> {
    const { data } = await apiClient.get<{ events: GoogleCalendarEventSummary[]; linked: boolean }>(
      '/events/google/range',
      { params: { from, to } },
    );
    return data;
  },

  async pushAllToGoogle(): Promise<{ total: number; pushed: number; failed: { eventId: string; title: string; error: string }[] }> {
    const { data } = await apiClient.post<{
      total: number;
      pushed: number;
      failed: { eventId: string; title: string; error: string }[];
    }>('/events/google/push-all');
    return data;
  },

  async create(payload: CreateEventPayload): Promise<EventDto> {
    const { data } = await apiClient.post<{ event: EventDto }>('/events', payload);
    return data.event;
  },

  async update(eventId: string, payload: UpdateEventPayload): Promise<EventDto> {
    const { data } = await apiClient.patch<{ event: EventDto }>(`/events/${eventId}`, payload);
    return data.event;
  },

  async remove(eventId: string, cascadeNote = false): Promise<void> {
    await apiClient.delete(`/events/${eventId}`, { params: { cascadeNote } });
  },

  async suggestSlot(
    startTime: string,
    endTime: string,
  ): Promise<{ found: false } | { found: true; startTime: string; endTime: string }> {
    const { data } = await apiClient.post<{ found: boolean; startTime?: string; endTime?: string }>(
      '/events/suggest-slot',
      { startTime, endTime },
    );
    return data.found
      ? { found: true, startTime: data.startTime as string, endTime: data.endTime as string }
      : { found: false };
  },

  async generateSleepSchedule(): Promise<SleepScheduleResult> {
    const { data } = await apiClient.post<SleepScheduleResult>('/events/sleep-schedule/generate');
    return data;
  },

  async getFutureSleepBlockCount(): Promise<number> {
    const { data } = await apiClient.get<{ count: number }>('/events/sleep-schedule/future-count');
    return data.count;
  },

  async regenerateSleepSchedule(): Promise<SleepScheduleResult> {
    const { data } = await apiClient.post<SleepScheduleResult>('/events/sleep-schedule/regenerate');
    return data;
  },
};

export interface SleepScheduleResult {
  created: number;
  skipped: { date: string; reason: string }[];
  alreadyExisted: number;
}
